/**
 * Divisions data source — 8 divisioni operative del LSSD.
 * Consumato da: Home (featured), Storia (grid), Divisioni index, Divisione detail.
 *
 * Ogni divisione ha:
 *  - code: slug URL-safe (PATROL, SEB, TSD, DB, AERO, CCD, IAB, PAB)
 *  - name: nome ufficiale completo
 *  - short: descrizione breve per card/chip
 *  - full: descrizione lunga per pagina dedicata
 *  - mission: statement di missione (1-2 frasi)
 *  - responsibilities: lista puntata di responsabilità principali
 *  - structure: com'è organizzata (chi la comanda, squadre)
 *  - tags: keyword operative
 *  - icon: nome lucide-react
 *  - accent: hex color accent per header (rimane in palette LSSD)
 *  - image: percorso immagine hero opzionale
 */

import {
  ShieldCheck, Users, Plane, Car, Search, Crosshair, Eye, Building2,
} from "lucide-react";

export const DIVISIONS = [
  {
    code: "PATROL",
    slug: "patrol",
    name: "Patrol Division",
    short: "La spina dorsale del Dipartimento. Pattugliamento capillare H24 in tutta la Contea.",
    full: "La Patrol Division è l'unità operativa più grande e visibile del LSSD. I Vice Sceriffi in pattuglia rappresentano il primo contatto tra il Dipartimento e la cittadinanza, garantendo copertura territoriale continua 24 ore su 24 su tutta l'estensione della Contea di Los Santos.",
    mission: "Assicurare la presenza dell'autorità sul territorio, essere prima risposta ad ogni chiamata di soccorso e mantenere l'ordine pubblico attraverso pattugliamento visibile e proattivo.",
    responsibilities: [
      "Pattugliamento territoriale H24 su tutta la Contea",
      "Prima risposta a chiamate 911 e interventi d'urgenza",
      "Controlli stradali di routine, identificazioni, contravvenzioni",
      "Supporto agli inseguimenti e ai posti di blocco",
      "Presidio degli eventi pubblici e assistenza alla comunità",
    ],
    structure: "Suddivisa in turni H24 (Alpha · Bravo · Charlie), coordinata da Sergenti di squadra e supervisionata dal Tenente di Divisione. Ogni pattuglia è dotata di MDT, body cam e collegamento radio dedicato.",
    tags: ["First Responders", "24/7", "Community"],
    icon: ShieldCheck,
    accent: "khaki",
    image: "/media/hero-explorer-still.jpg",
    gallery: [
      { src: "/media/hero-explorer-still.jpg", alt: "Patrol — Vice Sceriffo in servizio operativo" },
      { src: "/media/divisions/patrol-2.jpg", alt: "Patrol — Vice Sceriffi sulla scena di un'operazione notturna" },
      { src: "/media/divisions/patrol-3.jpg", alt: "Patrol — Charger a Pillbox Hill durante il temporale notturno" },
      { src: "/media/academy-patrol.png", alt: "Patrol — Unità sul terreno durante l'esercitazione" },
    ],
  },
  {
    code: "SEB",
    slug: "seb",
    name: "Special Enforcement Bureau",
    short: "Unità tattica ad alto rischio: ostaggi, terrorismo, irruzioni armate, protezione dignitari.",
    full: "Costituita nel 1968, la Special Enforcement Bureau è l'unità speciale del LSSD, la sua élite tattica. Interviene in situazioni ad alta intensità che richiedono equipaggiamento, addestramento e capacità operativa fuori dallo standard. Ogni operatore SEB è selezionato da Patrol e supera un training intensivo di 12 settimane.",
    mission: "Risolvere situazioni ad alto rischio dove la vita di cittadini, ostaggi od operatori è in pericolo immediato, con la minima escalation di forza necessaria.",
    responsibilities: [
      "Risoluzione crisi ostaggi (HRT — Hostage Rescue Team)",
      "Contrasto al terrorismo e alla criminalità organizzata armata",
      "Irruzioni tattiche su target ad alto valore",
      "Protezione dignitari (VIP Protection Detail)",
      "Supporto K-9 con unità cinofile specializzate",
    ],
    structure: "Squadra tactical operator (10-15 elementi), unità K-9, tiratori scelti (marksman). Il Comandante SEB risponde direttamente al Sotto Sceriffo.",
    tags: ["Tactical", "K-9", "Hostage Rescue", "VIP Protection"],
    icon: Crosshair,
    accent: "khaki",
    image: "/media/divisions/seb-1.jpg",
    gallery: [
      { src: "/media/divisions/seb-1.jpg", alt: "SEB — Bearcat armato in pattugliamento notturno sull'autostrada della Contea" },
    ],
  },
  {
    code: "TSD",
    slug: "tsd",
    name: "Traffic Services Detail",
    short: "Unità stradale e nautica specializzata: highway patrol, DUI, Mary Unit, ricostruzione incidenti.",
    full: "Il Traffic Services Detail è l'unità specializzata nella sicurezza stradale e nautica della Contea. Pattuglia autostrade, strade extraurbane, snodi ad alta intensità di traffico e le acque costiere di Los Santos. Al suo interno opera anche la Mary Unit, dedicata alla vigilanza sul mare, ai porti e alle emergenze in acqua. Gestisce indagini su incidenti gravi e mortali con ricostruzione tecnica della dinamica.",
    mission: "Ridurre incidenti, decessi e feriti gravi sulle strade e nelle acque della Contea attraverso deterrenza, controlli mirati, indagini tecniche accurate e presenza costante sui corridoi stradali e sul lungocosta.",
    responsibilities: [
      "Pattugliamento autostradale e extraurbano ad alta velocità",
      "Mary Unit — pattugliamento nautico, porti, salvataggio in acqua, contrasto al traffico via mare",
      "Controlli DUI (guida in stato di ebbrezza/alterazione)",
      "Ricostruzione tecnica di incidenti gravi (CIT — Crash Investigation Team)",
      "Coordinamento posti di blocco e scorte pesanti",
      "Formazione stradale e nautica per il resto del Dipartimento",
    ],
    structure: "Highway Patrol Unit, Mary Unit, DUI Enforcement Team, Crash Investigation Team. Il Tenente TSD coordina turni H24 sui principali corridoi stradali e sul lungocosta.",
    tags: ["Highway Patrol", "Mary Unit", "DUI Unit", "Crash Investigations"],
    icon: Car,
    accent: "khaki",
    image: "/media/divisions/tsd-1.jpg",
    gallery: [
      { src: "/media/divisions/tsd-1.jpg", alt: "TSD — Motociclisti in servizio in centro città" },
      { src: "/media/divisions/tsd-2.jpg", alt: "TSD — Pattuglia motorizzata sulle arterie principali" },
    ],
  },
  {
    code: "DB",
    slug: "db",
    name: "Detective Bureau",
    short: "Investigatori in borghese. Omicidi, narcotici, crimine organizzato, cold cases.",
    full: "Il Detective Bureau raccoglie gli investigatori del LSSD. Operano in borghese, spesso sotto copertura, per far luce sui crimini più complessi della Contea. La divisione lavora a stretto contatto con la Procura di San Andreas per portare i responsabili davanti alla giustizia.",
    mission: "Trasformare ogni scena del crimine, testimonianza e prova in un'imputazione solida, garantendo che nessun caso resti irrisolto per assenza di indagine.",
    responsibilities: [
      "Homicide Unit — indagini su omicidi e morti sospette",
      "Narcotics Unit — traffico di sostanze, spaccio, produzione",
      "Organized Crime — reti criminali strutturate, racket, riciclaggio",
      "Cold Case Unit — riapertura casi archiviati con nuove tecnologie",
      "Interfaccia diretta con la Procura per emissione mandati",
    ],
    structure: "Squadre specializzate (Homicide · Narcotics · Organized Crime · Cold Cases · Fraud). Il Capo del Detective Bureau riporta al Sotto Sceriffo. Accesso alle richieste di Mandato Procura tramite il portale.",
    tags: ["Homicide", "Narcotics", "Organized Crime", "Cold Cases"],
    icon: Search,
    accent: "khaki",
    image: "/media/divisions/db-1.jpg",
    gallery: [
      { src: "/media/divisions/db-1.jpg", alt: "Detective Bureau — Ispezione sulla scena del crimine dopo il tramonto" },
    ],
  },
  {
    code: "AERO",
    slug: "aero",
    name: "Aero Bureau",
    short: "Supporto aereo, ricerca dispersi, sorveglianza tattica e SAR sopra la Contea.",
    full: "L'Aero Bureau opera dall'alto sulla Contea di Los Santos dal 1972. La flotta comprende elicotteri Maverick e Buzzard equipaggiati per pattugliamento, ricerca-soccorso e supporto tattico. È un moltiplicatore di forza per Patrol, SEB e Detective Bureau.",
    mission: "Estendere la capacità operativa del Dipartimento sulla terza dimensione, riducendo tempi di risposta e garantendo copertura visiva anche nelle aree impervie della Contea.",
    responsibilities: [
      "Supporto agli inseguimenti terrestri (Pursuit Support)",
      "SAR — Search and Rescue in aree montane, costiere e desertiche",
      "Sorveglianza tattica ad alta quota (target tracking)",
      "Trasporto rapido di personale e materiale in emergenza",
      "Evacuazione medica (MEDEVAC) in coordinamento con EMS",
    ],
    structure: "Piloti brevettati, Tactical Flight Officer (TFO), meccanici di bordo. Base operativa: Pillbox Heliport (Vinewood). Coordina con Air Traffic Control della Contea.",
    tags: ["Maverick", "Air Support", "SAR", "MEDEVAC"],
    icon: Plane,
    accent: "khaki",
    image: "/media/aero-helicopter.png",
    gallery: [
      { src: "/media/aero-helicopter.png", alt: "Aero Bureau — Elicottero Maverick in servizio sopra la Contea" },
    ],
  },
  {
    code: "CCD",
    slug: "ccd",
    name: "Custody & Court Detail",
    short: "Sicurezza tribunali, gestione detenuti, trasferimenti penitenziari.",
    full: "La Custody & Court Detail garantisce la sicurezza fisica del sistema giudiziario della Contea e la corretta gestione dei soggetti in custodia. Gli operatori CCD sono responsabili di ogni fase che va dall'arresto alla comparizione in tribunale fino al trasferimento penitenziario.",
    mission: "Garantire che ogni soggetto arrestato sia trattato secondo la legge, che i tribunali operino in sicurezza e che nessun trasferimento comprometta la giustizia o l'incolumità pubblica.",
    responsibilities: [
      "Sicurezza fisica dei tribunali e delle udienze",
      "Gestione dei soggetti in custodia temporanea (holding cells)",
      "Trasferimenti detenuti tra strutture penitenziarie",
      "Scorte armate per soggetti ad alto profilo",
      "Coordinamento con la Procura per notifiche e comparizioni",
    ],
    structure: "Court Security Unit, Custody Officers, Transport Detail. Comandante CCD riferisce al Sotto Sceriffo.",
    tags: ["Court Security", "Custody", "Transfers"],
    icon: Building2,
    accent: "khaki",
  },
  {
    code: "IAB",
    slug: "iab",
    name: "Internal Affairs Bureau",
    short: "Affari Interni. Indaga sulla condotta degli operatori, garantisce l'integrità del Dipartimento.",
    full: "L'Internal Affairs Bureau è la coscienza del LSSD. Indaga in modo indipendente su presunte violazioni disciplinari, uso della forza, corruzione o condotta impropria degli operatori. Risponde direttamente allo Sceriffo per garantire imparzialità.",
    mission: "Assicurare che il Dipartimento mantenga i più alti standard etici e professionali, restando trasparente e responsabile davanti alla comunità che serve.",
    responsibilities: [
      "Indagini su condotta impropria e violazioni disciplinari",
      "Revisione degli usi della forza (Force Review Board)",
      "Gestione denunce cittadine contro operatori",
      "Audit dei procedimenti interni e delle procedure operative",
      "Interfaccia con Comando per sanzioni e provvedimenti disciplinari",
    ],
    structure: "Investigatori indipendenti selezionati dallo Sceriffo, revisori dei conti interni, ombudsman cittadino. IAB non risponde alla catena di comando operativa.",
    tags: ["Integrity", "Investigations", "Discipline"],
    icon: Eye,
    accent: "khaki",
  },
  {
    code: "PAB",
    slug: "pab",
    name: "Personnel Administration Bureau",
    short: "Risorse Umane del Dipartimento: reclutamenti, fascicoli, formazione, carriere.",
    full: "Il Personnel Administration Bureau è il motore amministrativo del LSSD. Gestisce l'intero ciclo di vita dell'operatore: dalla candidatura all'Accademia, dalla progressione di carriera al pensionamento. È il primo punto di contatto per chi vuole entrare nel Dipartimento.",
    mission: "Selezionare, formare e sviluppare gli operatori del LSSD, mantenendo aggiornati i fascicoli personali e supportando la carriera di ogni Vice Sceriffo.",
    responsibilities: [
      "Reclutamento e selezione dei candidati (colloqui, background check)",
      "Accademia di Formazione — 15 ore intensive teoria + pratica",
      "Gestione fascicoli personali, encomi e sanzioni",
      "Progressioni di carriera, promozioni ed esami di grado",
      "Gestione ferie, congedi, risorse mediche interne",
    ],
    structure: "Recruitment Team, Academy Instructors, HR Officers, Career Development. Il Capo PAB coordina con il Comando per tutte le decisioni di personale.",
    tags: ["Recruitment", "Training", "HR", "Academy"],
    icon: Users,
    accent: "khaki",
    image: "/media/academy-formation.png",
    gallery: [
      { src: "/media/academy-formation.png", alt: "PAB — Allievi in formazione presso l'Accademia" },
      { src: "/media/academy-ceremony.jpg", alt: "PAB — Cerimonia di giuramento dei nuovi Vice Sceriffi" },
      { src: "/media/academy-patrol.png", alt: "PAB — Esercitazione di pattugliamento durante il corso" },
    ],
  },
];

export const DIVISION_BY_SLUG = Object.fromEntries(DIVISIONS.map((d) => [d.slug, d]));
export const DIVISION_BY_CODE = Object.fromEntries(DIVISIONS.map((d) => [d.code.toLowerCase(), d]));

export function findDivision(param) {
  if (!param) return null;
  const key = String(param).toLowerCase();
  return DIVISION_BY_SLUG[key] || DIVISION_BY_CODE[key] || null;
}
